import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Download, Search, X } from 'lucide-react';
import { callSessions } from '@/api/callSessions';
import { useEffectiveCustomerId } from '@/lib/customer-context-store';
import type { CallSessionRow } from '@/api/callSessions';
import { ivrTrees } from '@/api/ivrTrees';
import { PageHeader } from '@/components/PageHeader';
import { DataTable } from '@/components/DataTable';
import { CallSessionDrawer } from './CallSessionDrawer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { DateTimePicker } from '@/components/DateTimePicker';
import { ResultChip } from './resultChip';
import { cn } from '@/lib/cn';

// ── Default date window ──────────────────────────────────────────────────────
//
// Call logs are dominated by "recent calls" — defaulting to the last 30
// days hits the customer's mental model on page load, keeps queries
// cheap, and Sam can always widen the range manually. The Clear-filters
// button resets to this same window rather than clearing entirely (an
// empty range is rarely what anyone wants).

function pad2(n: number): string { return String(n).padStart(2, '0'); }

function isoLocal(d: Date): string {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

function defaultDateFrom(): string {
  const d = new Date();
  d.setDate(d.getDate() - 30);
  d.setHours(0, 0, 0, 0);
  return isoLocal(d);
}

function defaultDateTo(): string {
  const d = new Date();
  d.setHours(23, 59, 0, 0);
  return isoLocal(d);
}

// ── Helpers ───────────────────────────────────────────────────────────────────

/**
 * Render an absolute timestamp like "May 19, 3:45 PM" with the
 * full ISO string as hover-tooltip. Returns an em-dash when null
 * (live calls have no end_at yet).
 */
function formatTimestamp(iso: string | null): { label: string; full: string } {
  if (!iso) return { label: '—', full: '' };
  const d = new Date(iso);
  const today = new Date();
  const sameYear = d.getFullYear() === today.getFullYear();
  const dateFmt = new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    ...(sameYear ? {} : { year: 'numeric' }),
  });
  const timeFmt = new Intl.DateTimeFormat(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  });
  return {
    label: `${dateFmt.format(d)}, ${timeFmt.format(d)}`,
    full: d.toLocaleString(),
  };
}

function fmtDuration(sec: number | null): string {
  if (sec === null) return '—';
  const m = Math.floor(sec / 60).toString().padStart(2, '0');
  const s = (sec % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
}

/**
 * Soften an all-uppercase CNAM display name to Title Case so a row
 * reads "Clarion Phone" instead of shouting "CLARION PHONE". Names
 * that already mix cases (e.g. "John Smith", "iPhone Bob") are left
 * untouched. Mixed-acronym names like "ABC INC" still get
 * downconverted — acceptable trade-off; the original value is
 * available on hover via the title attribute the cell sets.
 */
function softenAllCaps(name: string): string {
  if (!/^[A-Z][A-Z\s.&,\-']{1,}$/.test(name)) return name;
  return name
    .toLowerCase()
    .replace(/(^|\s|[-&.])([a-z])/g, (_m, sep, letter) => sep + letter.toUpperCase());
}

// ── Active filter chips ──────────────────────────────────────────────────────
//
// Rendered between the toolbar and the table. Each active filter shows
// as a small removable chip — click the × to clear that one filter
// only. The "Reset to last 30 days" button still clears everything at
// once. When no filters are active beyond the default window, the chip
// row collapses entirely.

function FilterChip({
  label,
  onRemove,
}: { label: string; onRemove: () => void }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 text-[11px] font-medium text-blue-700">
      <span>{label}</span>
      <button
        type="button"
        onClick={onRemove}
        className="rounded-full hover:bg-blue-100 transition-colors"
        aria-label={`Remove ${label} filter`}
      >
        <X className="h-3 w-3" />
      </button>
    </span>
  );
}

// ── Tree name cell — lazy resolve ─────────────────────────────────────────────

function TreeCell({ treeId }: { treeId: string | null }) {
  const { data: tree } = useQuery({
    queryKey: ['ivrTree', treeId],
    queryFn: () => ivrTrees.get(treeId!),
    enabled: !!treeId,
    staleTime: 5 * 60 * 1000,
  });
  if (!treeId) return <span className="text-slate-400">—</span>;
  if (!tree) return <span className="font-mono text-xs text-slate-400">…</span>;
  return (
    <a
      href={`/ivr/trees/${tree.id}`}
      onClick={(e) => e.stopPropagation()}
      className="text-sm text-primary hover:underline"
    >
      {tree.name}
    </a>
  );
}

// ── Live pill ─────────────────────────────────────────────────────────────────

function LivePill() {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 border border-emerald-200 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">
      <span className="relative flex h-1.5 w-1.5">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
        <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-500" />
      </span>
      Live
    </span>
  );
}

// ── Pagination control ───────────────────────────────────────────────────────
//
// Numbered pages with a window of ~5 around the current page, plus
// ellipsis when there are gaps. Page-size selector controls how many
// rows the query asks for.

const PAGE_SIZE_OPTIONS = [25, 50, 100, 250];

function buildPageList(current: number, total: number): Array<number | 'gap'> {
  // Always show: first page, last page, current ±2. Insert 'gap' markers
  // where consecutive shown pages are >1 apart.
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const shown = new Set<number>([1, total, current, current - 1, current + 1, current - 2, current + 2]);
  const sorted = Array.from(shown).filter((n) => n >= 1 && n <= total).sort((a, b) => a - b);
  const out: Array<number | 'gap'> = [];
  for (let i = 0; i < sorted.length; i++) {
    const n = sorted[i]!;
    if (i > 0 && n - sorted[i - 1]! > 1) out.push('gap');
    out.push(n);
  }
  return out;
}

function Pagination({
  page,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
}: {
  page: number; // 1-based
  pageSize: number;
  total: number;
  onPageChange: (n: number) => void;
  onPageSizeChange: (n: number) => void;
}) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const start = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const end = Math.min(total, page * pageSize);
  const pages = buildPageList(page, totalPages);
  return (
    <div className="mt-4 flex flex-wrap items-center justify-between gap-4 text-sm">
      <div className="text-slate-500">
        {total === 0 ? 'No calls' : <>Showing <strong>{start}</strong>–<strong>{end}</strong> of <strong>{total}</strong></>}
      </div>
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(page - 1)}
          disabled={page <= 1}
        >
          ‹ Prev
        </Button>
        {pages.map((p, i) =>
          p === 'gap' ? (
            <span key={`gap-${i}`} className="px-1 text-slate-400">…</span>
          ) : (
            <Button
              key={p}
              variant={p === page ? 'default' : 'outline'}
              size="sm"
              className="min-w-[2.25rem]"
              onClick={() => onPageChange(p)}
            >
              {p}
            </Button>
          ),
        )}
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(page + 1)}
          disabled={page >= totalPages}
        >
          Next ›
        </Button>
        <Select value={String(pageSize)} onValueChange={(v) => onPageSizeChange(Number(v))}>
          <SelectTrigger className="h-9 w-[5.5rem] text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PAGE_SIZE_OPTIONS.map((n) => (
              <SelectItem key={n} value={String(n)}>{n} / page</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export function CallLogList() {
  const effectiveCustomerId = useEffectiveCustomerId();
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Filters
  const [searchInput, setSearchInput] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  // Default to last 30 days on first render. Sam can widen manually,
  // and Clear-filters snaps back here rather than emptying the window.
  const [dateFrom, setDateFrom] = useState(() => defaultDateFrom());
  const [dateTo, setDateTo] = useState(() => defaultDateTo());
  const [treeFilter, setTreeFilter] = useState('');
  // Targeted partial-match filters — these are independent of the
  // free-text search box. Caller Number filters from_number only;
  // Dialed Number filters to_number only. Both are debounced via
  // the same 300 ms timer as the main search.
  const [callerInput, setCallerInput] = useState('');
  const [callerDebounced, setCallerDebounced] = useState('');
  const [dialedInput, setDialedInput] = useState('');
  const [dialedDebounced, setDialedDebounced] = useState('');
  // Call-type filter. '' = all calls (default).
  const [directionFilter, setDirectionFilter] = useState<'' | 'inbound' | 'outbound' | 'missed'>('');

  // Pagination
  const [pageSize, setPageSize] = useState(50);
  const [page, setPage] = useState(1); // 1-based

  // Debounce search + targeted number inputs → query, 300 ms.
  // Same timer covers all three so a rapid burst of typing across
  // fields settles into a single refetch.
  useEffect(() => {
    const t = setTimeout(() => {
      setSearchDebounced(searchInput.trim());
      setCallerDebounced(callerInput.trim());
      setDialedDebounced(dialedInput.trim());
      setPage(1); // any text-input change resets to page 1
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput, callerInput, dialedInput]);

  // Filter changes always reset to page 1
  useEffect(() => {
    setPage(1);
  }, [dateFrom, dateTo, treeFilter, directionFilter, pageSize, effectiveCustomerId]);

  const { data: treeList } = useQuery({
    queryKey: ['ivrTrees', 'picker', effectiveCustomerId],
    queryFn: () => ivrTrees.list({ limit: 200, customer_id: effectiveCustomerId ?? undefined }),
  });

  const queryParams = useMemo(() => ({
    from: dateFrom || undefined,
    to: dateTo || undefined,
    tree_id: treeFilter || undefined,
    search: searchDebounced || undefined,
    from_number: callerDebounced || undefined,
    to_number: dialedDebounced || undefined,
    direction: directionFilter || undefined,
    limit: pageSize,
    offset: (page - 1) * pageSize,
    customer_id: effectiveCustomerId ?? undefined,
  }), [
    dateFrom, dateTo, treeFilter, searchDebounced,
    callerDebounced, dialedDebounced, directionFilter,
    pageSize, page, effectiveCustomerId,
  ]);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['callSessions', queryParams],
    queryFn: () => callSessions.list(queryParams),
  });

  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;

  // Download CSV — fetches the full filtered set via auth-aware client.
  const [downloading, setDownloading] = useState(false);
  async function downloadCsv() {
    setDownloading(true);
    try {
      await callSessions.exportCsv({
        from: queryParams.from,
        to: queryParams.to,
        tree_id: queryParams.tree_id,
        search: queryParams.search,
        from_number: queryParams.from_number,
        to_number: queryParams.to_number,
        direction: queryParams.direction,
        customer_id: queryParams.customer_id,
      });
    } catch (err) {
      // Hand off to react-query's error boundary indirectly — the most
      // common failure is the 10 000-row cap, which the user should
      // already know to narrow filters for.
      // eslint-disable-next-line no-alert
      alert(err instanceof Error ? err.message : 'Download failed.');
    } finally {
      setDownloading(false);
    }
  }

  const columns = [
    {
      header: 'Caller Name',
      cell: (row: CallSessionRow) =>
        row.caller_display_name ? (
          // Don't bold — CNAM names often arrive ALL CAPS, and bolding
          // an all-caps name dominates the row. Title-case the value
          // (only when it's pure uppercase) and keep regular weight.
          <span
            className="text-sm text-slate-700"
            title={row.caller_display_name}
          >
            {softenAllCaps(row.caller_display_name)}
          </span>
        ) : (
          <span className="text-sm text-slate-400">—</span>
        ),
    },
    {
      header: 'Caller Number',
      cell: (row: CallSessionRow) => (
        <span className="font-mono text-sm text-slate-600">{row.from_number ?? '—'}</span>
      ),
    },
    {
      header: 'To',
      cell: (row: CallSessionRow) => (
        <span className="font-mono text-sm text-slate-600">{row.to_number ?? '—'}</span>
      ),
    },
    {
      header: 'Start Time',
      cell: (row: CallSessionRow) => {
        const { label, full } = formatTimestamp(row.started_at);
        return <span className="text-sm text-slate-600 whitespace-nowrap" title={full}>{label}</span>;
      },
    },
    {
      header: 'End Time',
      cell: (row: CallSessionRow) => {
        if (!row.ended_at) return <LivePill />;
        const { label, full } = formatTimestamp(row.ended_at);
        return <span className="text-sm text-slate-600 whitespace-nowrap" title={full}>{label}</span>;
      },
    },
    {
      header: 'Tree',
      cell: (row: CallSessionRow) => <TreeCell treeId={row.tree_id} />,
    },
    {
      header: 'Duration',
      cell: (row: CallSessionRow) =>
        !row.ended_at ? (
          <span className="text-sm text-slate-400">—</span>
        ) : (
          <span className="font-mono text-sm text-slate-600 tabular-nums">
            {fmtDuration(row.duration_sec)}
          </span>
        ),
    },
    {
      header: 'Result',
      cell: (row: CallSessionRow) => <ResultChip hangupCause={row.hangup_cause} />,
    },
  ];

  // "Has filters" = anything beyond the default 30-day window. Search,
  // a custom tree, targeted Caller/Dialed Number, Call Type, or a date
  // range outside the default counts. We compare against fresh defaults
  // each render — comparing reference equality wouldn't catch the case
  // where the user nudged the picker and then nudged back.
  const defaultFrom = defaultDateFrom().slice(0, 10);
  const defaultTo = defaultDateTo().slice(0, 10);
  const hasFilters = !!(
    searchInput
    || callerInput
    || dialedInput
    || directionFilter
    || treeFilter
    || (dateFrom && dateFrom.slice(0, 10) !== defaultFrom)
    || (dateTo && dateTo.slice(0, 10) !== defaultTo)
  );

  return (
    <>
      <PageHeader
        title="Call Log"
        description="Recent inbound calls — searchable, paginated, exportable."
      />

      {/* Toolbar — two rows:
            Row 1: free-text search + date range + Reset + CSV
            Row 2: targeted Caller Number / Dialed Number / Call Type / Tree
          Both rows always visible (no "Advanced" toggle) so every filter
          is discoverable at a glance. Wraps naturally on narrow screens. */}
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <div className="relative flex-1 min-w-[16rem] max-w-md">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
          <Input
            placeholder="Search by name, number, or tree…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="pl-8"
          />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-500 whitespace-nowrap">From</span>
          <DateTimePicker
            value={dateFrom}
            onChange={setDateFrom}
            defaultTimeOnDateSelect="00:00"
            placeholder="Start of range"
          />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-500 whitespace-nowrap">To</span>
          <DateTimePicker
            value={dateTo}
            onChange={setDateTo}
            defaultTimeOnDateSelect="23:59"
            placeholder="End of range"
          />
        </div>
        {hasFilters && (
          <Button
            variant="ghost"
            size="sm"
            className="text-xs text-slate-400"
            onClick={() => {
              setSearchInput('');
              setCallerInput('');
              setDialedInput('');
              setDirectionFilter('');
              // Snap the date range back to the rolling 30-day default
              // rather than emptying it. Empty dates would let the query
              // walk the entire history, which is almost never what
              // anyone wants.
              setDateFrom(defaultDateFrom());
              setDateTo(defaultDateTo());
              setTreeFilter('');
            }}
          >
            Reset to last 30 days
          </Button>
        )}
        <div className="ml-auto">
          <Button
            variant="outline"
            size="sm"
            onClick={downloadCsv}
            disabled={downloading || total === 0}
            title="Download all filtered rows as CSV (opens in Excel/Google Sheets)"
          >
            <Download className="h-4 w-4 mr-1.5" />
            {downloading ? 'Preparing…' : 'Download CSV'}
          </Button>
        </div>
      </div>

      {/* Row 2: targeted filters */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-500 whitespace-nowrap">Caller #</span>
          <Input
            placeholder="e.g. +1212"
            value={callerInput}
            onChange={(e) => setCallerInput(e.target.value)}
            className="h-9 w-40 text-sm"
          />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-500 whitespace-nowrap">Dialed #</span>
          <Input
            placeholder="e.g. +1845"
            value={dialedInput}
            onChange={(e) => setDialedInput(e.target.value)}
            className="h-9 w-40 text-sm"
          />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-500 whitespace-nowrap">Call type</span>
          <Select
            value={directionFilter || '__all__'}
            onValueChange={(v) => setDirectionFilter(
              v === '__all__' ? '' : (v as 'inbound' | 'outbound' | 'missed'),
            )}
          >
            <SelectTrigger className="h-9 w-36 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All calls</SelectItem>
              <SelectItem value="inbound">Inbound</SelectItem>
              <SelectItem value="outbound">Outbound</SelectItem>
              <SelectItem value="missed">Missed</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-500 whitespace-nowrap">Tree</span>
          <Select
            value={treeFilter || '__all__'}
            onValueChange={(v) => setTreeFilter(v === '__all__' ? '' : v)}
          >
            <SelectTrigger className="h-9 w-48 text-sm">
              <SelectValue placeholder="All trees" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All trees</SelectItem>
              {(treeList?.rows ?? []).map((t) => (
                <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Active filter chips — at-a-glance summary of what's narrowing
          the current results. Click any × to clear that single filter
          (the "Reset to last 30 days" button still clears them all).
          Date range chips only render when the range differs from the
          rolling 30-day default — otherwise every screen would carry
          a noisy "Last 30 days" chip that conveys no information. */}
      {hasFilters && (() => {
        const chips: Array<{ key: string; label: string; onRemove: () => void }> = [];
        if (searchInput) {
          chips.push({
            key: 'search',
            label: `Search: ${searchInput}`,
            onRemove: () => setSearchInput(''),
          });
        }
        if (callerInput) {
          chips.push({
            key: 'caller',
            label: `Caller: ${callerInput}`,
            onRemove: () => setCallerInput(''),
          });
        }
        if (dialedInput) {
          chips.push({
            key: 'dialed',
            label: `Dialed: ${dialedInput}`,
            onRemove: () => setDialedInput(''),
          });
        }
        if (directionFilter) {
          const labels = { inbound: 'Inbound', outbound: 'Outbound', missed: 'Missed' };
          chips.push({
            key: 'direction',
            label: labels[directionFilter],
            onRemove: () => setDirectionFilter(''),
          });
        }
        if (treeFilter) {
          const tree = (treeList?.rows ?? []).find((t) => t.id === treeFilter);
          chips.push({
            key: 'tree',
            label: `Tree: ${tree?.name ?? treeFilter}`,
            onRemove: () => setTreeFilter(''),
          });
        }
        // Date range: only show as a chip when the user has narrowed
        // OR widened away from the default window.
        if (dateFrom && dateFrom.slice(0, 10) !== defaultFrom) {
          chips.push({
            key: 'date-from',
            label: `From: ${dateFrom.slice(0, 10)}`,
            onRemove: () => setDateFrom(defaultDateFrom()),
          });
        }
        if (dateTo && dateTo.slice(0, 10) !== defaultTo) {
          chips.push({
            key: 'date-to',
            label: `To: ${dateTo.slice(0, 10)}`,
            onRemove: () => setDateTo(defaultDateTo()),
          });
        }
        if (chips.length === 0) return null;
        return (
          <div className="flex flex-wrap items-center gap-1.5 mb-3 text-[11px] text-slate-500">
            <span className="me-1">Active filters:</span>
            {chips.map((c) => (
              <FilterChip key={c.key} label={c.label} onRemove={c.onRemove} />
            ))}
          </div>
        );
      })()}

      {/* Table */}
      {isLoading ? (
        <div className="text-sm text-slate-400">Loading…</div>
      ) : error ? (
        <div role="alert" className="text-sm text-red-500 flex items-center gap-3">
          {error instanceof Error ? error.message : 'Failed to load call log.'}
          <Button variant="outline" size="sm" onClick={() => refetch()}>Retry</Button>
        </div>
      ) : (
        <>
          <DataTable
            rows={rows}
            columns={columns}
            onRowClick={(row) => setSelectedId(row.id)}
            empty={hasFilters ? 'No calls match the current filters.' : 'No calls yet.'}
          />
          <Pagination
            page={page}
            pageSize={pageSize}
            total={total}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
          />
        </>
      )}

      {/* Session detail drawer */}
      {selectedId && (
        <CallSessionDrawer
          sessionId={selectedId}
          onClose={() => setSelectedId(null)}
        />
      )}
    </>
  );
}
